/**
 * Solar Generation Data Parser
 * Parses Excel/CSV files containing hourly solar generation data
 */

import * as XLSX from 'xlsx';
import type { SolarGenerationData, SolarDataPoint } from '../types/solar.types';

// Expected column names (case-insensitive)
const TIMESTAMP_COLUMNS = ['timestamp', 'fecha_hora', 'fecha', 'datetime', 'date', 'time', 'tiempo'];
const GENERATION_COLUMNS = ['generacion_kw', 'solar_kw', 'generation_kw', 'power_kw', 'kw', 'potencia', 'consumo_propio', 'consumo propio', 'inyeccion', 'inyección'];

interface ParseResult {
  success: boolean;
  data?: SolarGenerationData;
  error?: string;
}

/**
 * Find a column by checking multiple possible names
 */
function findColumn(headers: string[], possibleNames: string[]): string | null {
  const normalizedHeaders = headers.map(h => h?.toLowerCase().trim().replace(/\s+/g, '_'));

  for (const name of possibleNames) {
    const index = normalizedHeaders.findIndex(h => h === name.toLowerCase());
    if (index !== -1) {
      return headers[index];
    }
  }

  // Try partial match
  for (const name of possibleNames) {
    const index = normalizedHeaders.findIndex(h => h?.includes(name.toLowerCase()));
    if (index !== -1) {
      return headers[index];
    }
  }

  return null;
}

/**
 * Parse timestamp from various formats
 */
function parseTimestamp(value: any, referenceYear?: number): Date | null {
  if (!value) return null;

  // Handle Excel serial date numbers
  if (typeof value === 'number') {
    // Excel serial date: days since 1900-01-01 (with incorrect leap year)
    const excelEpoch = new Date(1899, 11, 30);
    const date = new Date(excelEpoch.getTime() + value * 24 * 60 * 60 * 1000);
    return date;
  }

  // Handle string dates
  if (typeof value === 'string') {
    // Try various formats
    const date = new Date(value);
    if (!isNaN(date.getTime())) return date;

    // Try DD/MM/YYYY HH:MM format
    const dmyMatch = value.match(/(\d{1,2})\/(\d{1,2})\/(\d{4})\s*(\d{1,2})?:?(\d{2})?/);
    if (dmyMatch) {
      const [, day, month, year, hour = '0', minute = '0'] = dmyMatch;
      return new Date(parseInt(year), parseInt(month) - 1, parseInt(day), parseInt(hour), parseInt(minute));
    }

    // Try YYYY-MM-DD HH:MM format
    const ymdMatch = value.match(/(\d{4})-(\d{1,2})-(\d{1,2})\s*(\d{1,2})?:?(\d{2})?/);
    if (ymdMatch) {
      const [, year, month, day, hour = '0', minute = '0'] = ymdMatch;
      return new Date(parseInt(year), parseInt(month) - 1, parseInt(day), parseInt(hour), parseInt(minute));
    }

    // Try PVSOL format: DD.MM. HH:MM (no year - use reference year or current year)
    const pvsolMatch = value.match(/(\d{1,2})\.(\d{1,2})\.\s*(\d{1,2}):(\d{2})/);
    if (pvsolMatch) {
      const [, day, month, hour, minute] = pvsolMatch;
      const year = referenceYear || new Date().getFullYear();
      return new Date(year, parseInt(month) - 1, parseInt(day), parseInt(hour), parseInt(minute));
    }
  }

  // Handle Date objects
  if (value instanceof Date) {
    return value;
  }

  return null;
}

/**
 * Parse generation value (kW)
 */
function parseGenerationValue(value: any): number {
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const cleaned = value.replace(/[^\d.-]/g, '');
    const num = parseFloat(cleaned);
    return isNaN(num) ? 0 : num;
  }
  return 0;
}

/**
 * Calculate statistics from solar data
 */
function calculateStatistics(data: SolarDataPoint[]): SolarGenerationData['statistics'] {
  if (data.length === 0) return undefined;

  // Daily averages
  const dailyTotals: { [date: string]: number } = {};
  const hourlyTotals: { [hour: number]: { sum: number; count: number } } = {};
  const monthlyTotals: { [month: string]: number } = {};

  let peakGeneration = 0;
  let peakHour = '';

  data.forEach(point => {
    const date = new Date(point.timestamp);
    const dateKey = date.toISOString().split('T')[0];
    const hour = date.getHours();
    const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

    // Daily totals
    dailyTotals[dateKey] = (dailyTotals[dateKey] || 0) + point.generation_kw;

    // Hourly profile
    if (!hourlyTotals[hour]) {
      hourlyTotals[hour] = { sum: 0, count: 0 };
    }
    hourlyTotals[hour].sum += point.generation_kw;
    hourlyTotals[hour].count++;

    // Monthly totals
    monthlyTotals[monthKey] = (monthlyTotals[monthKey] || 0) + point.generation_kw;

    // Peak tracking
    if (point.generation_kw > peakGeneration) {
      peakGeneration = point.generation_kw;
      peakHour = point.timestamp;
    }
  });

  const dailyValues = Object.values(dailyTotals);
  const dailyAvgKwh = dailyValues.length > 0
    ? dailyValues.reduce((a, b) => a + b, 0) / dailyValues.length
    : 0;

  // Find peak hour of day (average)
  let maxHourlyAvg = 0;
  let peakHourOfDay = '12:00';
  Object.entries(hourlyTotals).forEach(([hour, stats]) => {
    const avg = stats.sum / stats.count;
    if (avg > maxHourlyAvg) {
      maxHourlyAvg = avg;
      peakHourOfDay = `${hour.padStart(2, '0')}:00`;
    }
  });

  return {
    daily_avg_kwh: dailyAvgKwh,
    monthly_totals: Object.entries(monthlyTotals)
      .map(([month, kwh]) => ({ month, generation_kwh: kwh }))
      .sort((a, b) => a.month.localeCompare(b.month)),
    peak_hour: peakHourOfDay,
    peak_generation_kw: peakGeneration,
  };
}

/**
 * Detect if this is a PVSOL format file and find the data start row
 */
function detectPVSOLFormat(jsonData: any[][]): { isPVSOL: boolean; dataStartRow: number; referenceYear: number } {
  // PVSOL files have monthly summaries (Ene, Feb, Mar...) before hourly data
  // Hourly data starts with timestamps like "01.01. 00:00"
  const monthNames = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];

  let dataStartRow = 1; // Default: data starts at row 1 (after header)
  let isPVSOL = false;

  for (let i = 1; i < Math.min(jsonData.length, 30); i++) {
    const row = jsonData[i];
    if (!row || row.length === 0) continue;

    const firstCell = String(row[0] || '').toLowerCase().trim();

    // Check if this row is a month summary (PVSOL format indicator)
    if (monthNames.some(m => firstCell === m || firstCell.startsWith(m))) {
      isPVSOL = true;
      continue;
    }

    // Check if this row starts with a PVSOL timestamp (DD.MM. HH:MM)
    if (firstCell.match(/^\d{1,2}\.\d{1,2}\.\s*\d{1,2}:\d{2}/)) {
      dataStartRow = i;
      isPVSOL = true;
      break;
    }
  }

  // Default reference year - use current year or infer from context
  const referenceYear = new Date().getFullYear();

  return { isPVSOL, dataStartRow, referenceYear };
}

/**
 * Parse an Excel or CSV file containing solar generation data
 */
export async function parseSolarFile(file: File): Promise<ParseResult> {
  try {
    const arrayBuffer = await file.arrayBuffer();
    const workbook = XLSX.read(arrayBuffer, {
      type: 'array',
      cellDates: true,
      dateNF: 'yyyy-mm-dd hh:mm:ss'
    });

    // Get first sheet
    const sheetName = workbook.SheetNames[0];
    const sheet = workbook.Sheets[sheetName];

    // Convert to JSON with headers
    const jsonData = XLSX.utils.sheet_to_json(sheet, { header: 1 }) as any[][];

    if (jsonData.length < 2) {
      return { success: false, error: 'El archivo está vacío o no tiene datos suficientes' };
    }

    // First row is headers (trim trailing spaces)
    const headers = jsonData[0].map(h => String(h || '').trim());

    // Detect PVSOL format
    const { isPVSOL, dataStartRow, referenceYear } = detectPVSOLFormat(jsonData);

    // Find required columns
    const timestampCol = findColumn(headers, TIMESTAMP_COLUMNS);
    const generationCol = findColumn(headers, GENERATION_COLUMNS);

    if (!timestampCol) {
      return {
        success: false,
        error: `No se encontró columna de timestamp. Columnas esperadas: ${TIMESTAMP_COLUMNS.join(', ')}. Columnas encontradas: ${headers.join(', ')}`
      };
    }

    if (!generationCol) {
      return {
        success: false,
        error: `No se encontró columna de generación solar. Columnas esperadas: ${GENERATION_COLUMNS.join(', ')}. Columnas encontradas: ${headers.join(', ')}`
      };
    }

    const timestampIndex = headers.indexOf(timestampCol);
    const generationIndex = headers.indexOf(generationCol);

    // Parse data rows (starting from detected data start row for PVSOL files)
    const dataPoints: SolarDataPoint[] = [];
    const errors: string[] = [];
    const startRow = isPVSOL ? dataStartRow : 1;

    for (let i = startRow; i < jsonData.length; i++) {
      const row = jsonData[i];
      if (!row || row.length === 0) continue;

      const rawTimestamp = row[timestampIndex];
      const rawGeneration = row[generationIndex];

      // Skip rows that don't have valid timestamp format (like summary rows)
      if (typeof rawTimestamp === 'string') {
        const trimmed = rawTimestamp.toLowerCase().trim();
        // Skip month names, headers, units
        if (['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic', 'kwh', 'kw', 'tiempo', ''].some(s => trimmed === s || trimmed.startsWith(s + ' '))) {
          continue;
        }
      }

      const timestamp = parseTimestamp(rawTimestamp, referenceYear);
      const generation = parseGenerationValue(rawGeneration);

      if (!timestamp) {
        errors.push(`Fila ${i + 1}: timestamp inválido "${rawTimestamp}"`);
        continue;
      }

      // Validate generation value (should be non-negative)
      if (generation < 0) {
        errors.push(`Fila ${i + 1}: generación negativa (${generation}), usando 0`);
      }

      dataPoints.push({
        timestamp: timestamp.toISOString(),
        generation_kw: Math.max(0, generation),
      });
    }

    if (dataPoints.length === 0) {
      return {
        success: false,
        error: `No se pudieron parsear datos válidos. Errores: ${errors.slice(0, 5).join('; ')}`
      };
    }

    // Sort by timestamp
    dataPoints.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());

    // Calculate summary statistics
    const generations = dataPoints.map(d => d.generation_kw);
    const totalGenerationKwh = generations.reduce((a, b) => a + b, 0); // Assuming hourly data
    const avgGeneration = totalGenerationKwh / dataPoints.length;
    const peakGeneration = Math.max(...generations);

    // Calculate capacity factor (assuming peak is nameplate capacity)
    // This is an estimate - actual capacity should come from configuration
    const capacityFactor = peakGeneration > 0 ? (avgGeneration / peakGeneration) * 100 : 0;

    const result: SolarGenerationData = {
      totalRecords: dataPoints.length,
      avgGeneration,
      peakGeneration,
      totalGenerationKwh,
      capacityFactor,
      startDate: dataPoints[0].timestamp,
      endDate: dataPoints[dataPoints.length - 1].timestamp,
      data: dataPoints,
      statistics: calculateStatistics(dataPoints),
    };

    // Log warnings if any
    if (errors.length > 0) {
      console.warn('Solar parser warnings:', errors.slice(0, 10));
    }

    return { success: true, data: result };

  } catch (error) {
    console.error('Error parsing solar file:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Error desconocido al procesar el archivo'
    };
  }
}

/**
 * Validate that solar data aligns with consumption data
 */
export function validateSolarAlignment(
  solarData: SolarGenerationData,
  consumptionStartDate: string,
  consumptionEndDate: string
): { valid: boolean; warnings: string[] } {
  const warnings: string[] = [];

  const solarStart = new Date(solarData.startDate);
  const solarEnd = new Date(solarData.endDate);
  const consStart = new Date(consumptionStartDate);
  const consEnd = new Date(consumptionEndDate);

  // Check date overlap
  if (solarEnd < consStart || solarStart > consEnd) {
    return {
      valid: false,
      warnings: ['Los datos solares no tienen fechas que coincidan con los datos de consumo'],
    };
  }

  // Check for partial overlap
  if (solarStart > consStart) {
    warnings.push(`Los datos solares empiezan después que los de consumo (${solarStart.toLocaleDateString()} vs ${consStart.toLocaleDateString()})`);
  }

  if (solarEnd < consEnd) {
    warnings.push(`Los datos solares terminan antes que los de consumo (${solarEnd.toLocaleDateString()} vs ${consEnd.toLocaleDateString()})`);
  }

  // Check expected record count (8760 hours in a year)
  const expectedHours = Math.round((solarEnd.getTime() - solarStart.getTime()) / (1000 * 60 * 60));
  if (solarData.totalRecords < expectedHours * 0.95) {
    warnings.push(`Faltan datos: se encontraron ${solarData.totalRecords} registros, se esperaban aproximadamente ${expectedHours}`);
  }

  return { valid: true, warnings };
}

/**
 * Generate a sample solar data file template
 */
export function generateSolarTemplate(): Blob {
  const headers = ['timestamp', 'generacion_kw'];
  const sampleData = [
    ['2024-01-01 00:00', 0],
    ['2024-01-01 01:00', 0],
    ['2024-01-01 06:00', 0],
    ['2024-01-01 07:00', 50],
    ['2024-01-01 08:00', 150],
    ['2024-01-01 09:00', 280],
    ['2024-01-01 10:00', 380],
    ['2024-01-01 11:00', 450],
    ['2024-01-01 12:00', 500],
    ['2024-01-01 13:00', 480],
    ['2024-01-01 14:00', 420],
    ['2024-01-01 15:00', 350],
    ['2024-01-01 16:00', 250],
    ['2024-01-01 17:00', 150],
    ['2024-01-01 18:00', 50],
    ['2024-01-01 19:00', 0],
    ['2024-01-01 20:00', 0],
  ];

  const ws = XLSX.utils.aoa_to_sheet([headers, ...sampleData]);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Solar Generation');

  const buffer = XLSX.write(wb, { type: 'array', bookType: 'xlsx' });
  return new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
}
